// src/pages/FundPlanner/reports/exportPortfolioCashFlowsPdf.js
import dayjs from "lib/dayjs";
import { createCompassPdf } from "reporting/pdf/createCompassPdf";
import { addKpiCards } from "reporting/pdf/addKpiCards";
import { addReportTable } from "reporting/pdf/addReportTable";
import {
  addKeyValueBlock,
  PDF_CONTENT_GAP,
  addSectionTitle,
  ensurePageSpace,
} from "reporting/pdf/pdfLayout";
import {
  fmtCurrency,
  fmtDate,
  safeFilePart,
} from "reporting/pdf/pdfFormatters";
import { PDF_CHART_SERIES, PDF_STONE } from "reporting/pdf/pdfTheme";

const CASH_FLOW_COLUMNS = [
  { key: "date", label: "Date", align: "left", format: (value) => fmtDate(value) },
  { key: "label", label: "Period", align: "left" },
  { key: "type", label: "Type", align: "left" },
  { key: "investmentType", label: "Investment", align: "left" },
  { key: "fundName", label: "Fund", align: "left" },
  { key: "cusipOrId", label: "CUSIP / ID", align: "left" },
  { key: "amount", label: "Cash Flow", align: "right", format: (value) => fmtCurrency(value, 2) },
];

const NOTE_COLUMNS = [
  { key: "note", label: "Projection Note", align: "left" },
];

function buildKpis(totals = {}) {
  return [
    { label: "Principal", value: fmtCurrency(totals.principal, 2) },
    { label: "Interest / Income", value: fmtCurrency(totals.interest, 2) },
    { label: "Scheduled Total", value: fmtCurrency(totals.scheduledTotal, 2) },
  ];
}

export function withoutDemandLiquidity(cashFlows = {}) {
  return {
    ...cashFlows,
    totals: {
      ...(cashFlows.totals || {}),
      demandLiquidity: 0,
      totalIncludingLiquidity: num(cashFlows.totals?.scheduledTotal),
    },
    chartRows: (cashFlows.chartRows || []).map((row) => ({
      ...row,
      demandLiquidity: 0,
      totalIncludingLiquidity: num(row?.scheduledTotal),
    })),
    detailRows: (cashFlows.detailRows || []).filter(
      (row) => String(row?.type || "").trim().toLowerCase() !== "demand liquidity"
    ),
  };
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatKmb(value) {
  const n = Number(value) || 0;
  const a = Math.abs(n);

  if (a >= 1e9) return `${(n / 1e9).toFixed(a >= 10e9 ? 0 : 1)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 10e6 ? 0 : 1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(a >= 10e3 ? 0 : 1)}K`;

  return `${Math.round(n)}`;
}

function getNiceTickStep(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;

  const exponent = Math.floor(Math.log10(n));
  const magnitude = 10 ** exponent;
  const normalized = n / magnitude;

  if (normalized <= 1) return magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 2.5) return 2.5 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

function getTightAxisMax(value, tickCount = 5) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;

  const padded = n * 1.05;
  const step = getNiceTickStep(padded / Math.max(1, tickCount - 1));

  return Math.ceil(padded / step) * step;
}

function buildTicks(maxValue, tickCount = 5) {
  const max = getTightAxisMax(maxValue, tickCount);
  const step = max / Math.max(1, tickCount - 1);

  return Array.from({ length: tickCount }, (_, index) => step * index);
}

function drawLegendItem(doc, x, y, color, label) {
  doc.setDrawColor(...color);
  doc.setFillColor(...color);
  doc.rect(x, y - 8, 12, 7, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(70);
  doc.text(label, x + 18, y - 2);
}

function addCashFlowChart(ctx, rows = [], startY) {
  const { doc, margins, usableWidth } = ctx;
  const data = (Array.isArray(rows) ? rows : [])
    .filter((row) => {
      return (
        num(row?.principal) !== 0 ||
        num(row?.interest) !== 0 ||
        num(row?.demandLiquidity) !== 0
      );
    })
    .map((row) => ({
      label: row?.label || row?.bucketKey || "",
      principal: num(row?.principal),
      interest: num(row?.interest),
      demandLiquidity: num(row?.demandLiquidity),
    }));

  const chartHeight = 230;
  const neededHeight = chartHeight + 42;
  let y = ensurePageSpace(ctx, startY, neededHeight);

  y = addSectionTitle(ctx, "Projected Cash Flows", y);

  if (!data.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text("No chart data available.", margins.left, y);
    return y + PDF_CONTENT_GAP;
  }

  const chartX = margins.left;
  const chartY = y;
  const chartW = usableWidth;
  const plotLeft = chartX + 42;
  const plotRight = chartX + chartW - 10;
  const plotTop = chartY + 8;
  const plotBottom = chartY + chartHeight - 44;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const maxValue = Math.max(
    ...data.map((row) => row.principal + row.interest),
    ...data.map((row) => row.demandLiquidity),
    1
  );
  const ticks = buildTicks(maxValue, 5);
  const yMax = ticks[ticks.length - 1] || 1;
  const yPos = (value) =>
    plotBottom - (Math.max(0, value) / yMax) * plotH;
  const barSlot = data.length > 0 ? plotW / data.length : plotW;
  const xPos = (index) => plotLeft + barSlot * index + barSlot / 2;
  const hasPrincipal = data.some((row) => row.principal > 0);
  const hasInterest = data.some((row) => row.interest > 0);
  const hasScheduled = hasPrincipal || hasInterest;
  const hasDemandLiquidity = data.some((row) => row.demandLiquidity > 0);
  const grouped = hasScheduled && hasDemandLiquidity;
  const barW = grouped
    ? Math.max(2, Math.min(10, barSlot * 0.24))
    : Math.max(2.5, Math.min(14, barSlot * 0.56));

  const colors = {
    principal: PDF_CHART_SERIES[0],
    interest: PDF_CHART_SERIES[1],
    liquidity: PDF_CHART_SERIES[2],
    grid: PDF_STONE.grid,
    axis: PDF_STONE.axis,
  };

  doc.setDrawColor(215);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(chartX, chartY, chartW, chartHeight, 5, 5, "S");

  ticks.forEach((tick) => {
    const yy = yPos(tick);

    doc.setDrawColor(...colors.grid);
    doc.setLineWidth(0.35);
    doc.line(plotLeft, yy, plotRight, yy);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(85);
    doc.text(`$${formatKmb(tick)}`, plotLeft - 7, yy + 2.5, {
      align: "right",
    });
  });

  doc.setDrawColor(...colors.axis);
  doc.setLineWidth(0.6);
  doc.line(plotLeft, plotBottom, plotRight, plotBottom);
  doc.line(plotLeft, plotTop, plotLeft, plotBottom);

  data.forEach((row, index) => {
    const x = xPos(index);
    const scheduledBarX = grouped ? x - barW - 1 : x - barW / 2;
    const liquidityBarX = grouped ? x + 1 : x - barW / 2;
    const principalTop = yPos(row.principal);
    const interestTop = yPos(row.principal + row.interest);
    const liquidityTop = yPos(row.demandLiquidity);

    if (row.principal > 0) {
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.25);
      doc.setFillColor(...colors.principal);
      doc.rect(
        scheduledBarX,
        principalTop,
        barW,
        plotBottom - principalTop,
        "FD"
      );
    }

    if (row.interest > 0) {
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.25);
      doc.setFillColor(...colors.interest);
      doc.rect(
        scheduledBarX,
        interestTop,
        barW,
        principalTop - interestTop,
        "FD"
      );
    }

    if (hasDemandLiquidity && row.demandLiquidity > 0) {
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.25);
      doc.setFillColor(...colors.liquidity);
      doc.rect(
        liquidityBarX,
        liquidityTop,
        barW,
        plotBottom - liquidityTop,
        "FD"
      );
    }
  });

  const labelEvery = Math.max(1, Math.ceil(data.length / 8));
  data.forEach((row, index) => {
    if (index % labelEvery !== 0 && index !== data.length - 1) return;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(85);
    doc.text(row.label, xPos(index), plotBottom + 12, {
      align: "center",
    });
  });

  const legendY = chartY + chartHeight - 14;
  let legendX = plotLeft;

  if (hasPrincipal) {
    drawLegendItem(doc, legendX, legendY, colors.principal, "Principal");
    legendX += 78;
  }

  if (hasInterest) {
    drawLegendItem(doc, legendX, legendY, colors.interest, "Interest / Income");
    legendX += 102;
  }

  if (hasDemandLiquidity) {
    drawLegendItem(doc, legendX, legendY, colors.liquidity, "Demand Liquidity");
  }

  return chartY + chartHeight + PDF_CONTENT_GAP;
}

export function renderPortfolioCashFlowsPdf(ctx, {
  cashFlows,
  asOfDate = "",
  projectionEndDate = "",
  frequencyLabel = "",
  portfolioName = "",
  scopeType = "portfolio",
  scopeName = "",
  showFundColumn = true,
} = {}) {
  const safeCashFlows = withoutDemandLiquidity(cashFlows || {
    detailRows: [],
    notes: [],
    totals: {},
  });

  const { margins, usableWidth } = ctx;
  let y = margins.top + 2;

  y = addKeyValueBlock(
    ctx,
    [
      {
        label: scopeType === "fund" ? "Fund" : "Portfolio",
        value: scopeName || portfolioName || (scopeType === "fund" ? "Current Fund" : "Current Portfolio"),
      },
      {
        label: "Projection",
        value: projectionEndDate
          ? `${fmtDate(asOfDate)} through ${fmtDate(projectionEndDate)}`
          : fmtDate(asOfDate),
      },
      { label: "Frequency", value: frequencyLabel || "Monthly" },
    ],
    y,
    {
      columns: 1,
      labelWidth: 58,
      rowHeight: 14,
      spaceAfter: 10,
    }
  );

  y = addKpiCards(ctx, buildKpis(safeCashFlows.totals), y);
  y = addCashFlowChart(ctx, safeCashFlows.chartRows || [], y);

  y = addReportTable(
    ctx,
    {
      columns: showFundColumn
        ? CASH_FLOW_COLUMNS
        : CASH_FLOW_COLUMNS.filter((column) => column.key !== "fundName"),
      rows: safeCashFlows.detailRows || [],
      tableWidth: usableWidth,
      fontSize: 6.2,
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 40 },
        2: { cellWidth: 72 },
        3: { cellWidth: 86 },
        4: { cellWidth: 108 },
        5: { cellWidth: 80 },
        6: { cellWidth: 98 },
      },
      styles: {
        cellPadding: 2,
        overflow: "linebreak",
      },
      headStyles: {
        fontSize: 6.2,
        cellPadding: 2,
      },
      rowPageBreak: "avoid",
    },
    y
  );

  if (safeCashFlows.notes?.length) {
    addReportTable(
      ctx,
      {
        title: "Projection Notes",
        columns: NOTE_COLUMNS,
        rows: safeCashFlows.notes.map((note) => ({ note })),
        fontSize: 7.2,
        styles: {
          cellPadding: 2.5,
          overflow: "linebreak",
        },
      },
      y
    );
  }
}

export async function exportPortfolioCashFlowsPdf({
  save = true,
  ...args
} = {}) {
  const ctx = await createCompassPdf({
    orgId: args.orgId,
    reportTitle: args.scopeType === "fund" ? "FundPlanner - Fund Cash Flows" : "FundPlanner - Portfolio Cash Flows",
    orgName: args.orgName || args.scopeName || args.portfolioName,
    asOfLabel: args.asOfDate ? `As of ${fmtDate(args.asOfDate)}` : "",
    createdStamp: new Date(),
    orientation: "portrait",
    branding: "unbranded",
    contentMargins: {
      top: 102,
      bottom: 58,
    },
  });

  renderPortfolioCashFlowsPdf(ctx, args);

  ctx.finalize();

  const { doc } = ctx;

  const fileName = `FundPlanner_Cash_Flows_${safeFilePart(
    args.orgName || args.scopeName || args.portfolioName || (args.scopeType === "fund" ? "Fund" : "Portfolio")
  )}_${safeFilePart(args.scopeName || args.portfolioName || "Cash_Flows")}_${dayjs(
    args.projectionEndDate || args.asOfDate || new Date()
  ).format("YYYY-MM-DD")}.pdf`;

  if (save) {
    doc.save(fileName);
  }

  return {
    doc,
    fileName,
  };
}

export default exportPortfolioCashFlowsPdf;
